import { useState, useEffect, useCallback, useMemo } from 'react'

import { MAX_PINNED_FAVORITES_DESKTOP, MAX_PINNED_FAVORITES_MOBILE } from '../constants/favoritesConfig.js'

/**
 * Manages pinned favorite products for the favorites compare area.
 * Desktop: max 3 pinned | Mobile: max 2 pinned
 *
 * @param {{ favoriteUuids?: Set<string> | string[] | null }} params
 * @returns {{
 *   pinnedUuids: string[],
 *   canPin: (uuid: string) => boolean,
 *   togglePin: (uuid: string) => void,
 *   unpinAll: () => void,
 *   isPinned: (uuid: string) => boolean,
 * }}
 */
export function useFavoritesPinning({ favoriteUuids }) {
  const [pinnedUuids, setPinnedUuids] = useState([])

  const favoriteUuidSet = favoriteUuids instanceof Set
    ? favoriteUuids
    : new Set(Array.isArray(favoriteUuids) ? favoriteUuids : [])

  const maxPinned = useMemo(() => {
    if (typeof window === 'undefined') return MAX_PINNED_FAVORITES_DESKTOP
    const isMobile = window.innerWidth < 768
    return isMobile ? MAX_PINNED_FAVORITES_MOBILE : MAX_PINNED_FAVORITES_DESKTOP
  }, [])

  const canPin = useCallback(
    (uuid) => {
      if (!uuid || !favoriteUuidSet.has(uuid)) return false
      const isPinnedAlready = pinnedUuids.includes(uuid)
      if (isPinnedAlready) return true
      return pinnedUuids.length < maxPinned
    },
    [favoriteUuidSet, pinnedUuids, maxPinned],
  )

  const togglePin = useCallback(
    (uuid) => {
      if (!uuid || !favoriteUuidSet.has(uuid)) return
      setPinnedUuids((prev) => {
        if (prev.includes(uuid)) {
          return prev.filter((u) => u !== uuid)
        }
        if (prev.length >= maxPinned) {
          return prev
        }
        return [...prev, uuid]
      })
    },
    [favoriteUuidSet, maxPinned],
  )

  const unpinAll = useCallback(() => {
    setPinnedUuids([])
  }, [])

  const isPinned = useCallback(
    (uuid) => pinnedUuids.includes(uuid),
    [pinnedUuids],
  )

  useEffect(() => {
    setPinnedUuids((prev) =>
      prev.filter((uuid) => favoriteUuidSet.has(uuid))
    )
  }, [favoriteUuidSet])

  return {
    pinnedUuids,
    isPinned,
    canPin,
    togglePin,
    unpinAll,
  }
}
